import {AllPropertyForecastLocksQuery, VersionType} from "../../../__generated__/generated_types";
import {SimpleHoTCellType, SimpleHoTSelection} from "./SimpleHoTWrapper";

export interface GroupedLineItem {
    lineItemId: string;
    propertyId: string;
    destinationAccountId: string;
    propertyName: string;
    accountLabel: string;
    description: string;
    locked: boolean;
    valuesLocked: boolean;
    values: (number | null)[];
}

export interface GroupedOperationalDriver {
    driverId: string;
    propertyId: string;
    destinationAccountId: string;
    operationalMetricTypeId: string;
    operationalMetric: string;
    secondaryOperationalMetricTypeId: string | null;
    secondaryOperationalMetric: string | null;
    propertyName: string;
    accountLabel: string;
    name: string;
    locked: boolean;
    valuesLocked: boolean;
    configLocked: boolean;
    amounts: (number | null)[];
    percents: (number | null)[];
    delayRecognition: number;
    cumSum: boolean | null;
    nonProrated: boolean;
    minValue: number | null;
    maxValue: number | null;
    year: number;
    versionType: VersionType;
}

export interface GroupedCustomDriver {
    driverId: string;
    propertyId: string;
    destinationAccountId: string;
    itemName: string;
    propertyName: string;
    accountLabel: string;
    locked: boolean;
    valuesLocked: boolean;
    amounts: (number | null)[];
    percents: (number | null)[];
    counts: (number | null)[];
}



export function addToArray(sumArr: number[], toAdd: number[]): void {
    const minLen = Math.min(sumArr.length, toAdd.length);
    for(let i = 0; i < minLen; i++) {
        sumArr[i]! += toAdd[i]!;
    }
}

export function divideArray(arr: number[], divisor: number): number[] {
    if(divisor === 0) {
        return new Array(arr.length).fill(0);
    }
    return arr.map(a => +((a / divisor).toFixed(2)));
}

export function toLockLookup(forecastPeriod: VersionType.Budget | VersionType.Reforecast, data: AllPropertyForecastLocksQuery | undefined): Set<string> {
    const ret = new Set<string>();
    if(data === undefined) {
        return ret;
    }

    for(const lock of data.allPropertyForecastLocks) {
        if(forecastPeriod === VersionType.Budget && lock.budgetLocked) {
            ret.add(lock.propertyId);
        } else if(forecastPeriod === VersionType.Reforecast && lock.reforecastLocked) {
            ret.add(lock.propertyId);
        }
    }

    return ret;
}

export function cellTypeToNumber(cellType: SimpleHoTCellType): number | null {
    if(cellType === null) {
        return null;
    }
    if(typeof cellType === "number") {
        return cellType;
    }
    const val = parseFloat(cellType);
    if(!Number.isNaN(val)) {
        return val;
    }
    return null;
}

// We talked about different ways to handle different multiselection scenarios, and this is the current logic
// if both a parent row and any of its children are part of the selection array, discard the parent row so we only
// operate on the children in the selection. This is because we iterate over parentRow.children when updating,
// and if only two children are selected, we do not want the parent row to trigger updates on ALL children
export function filterSelectedLineItems(selection: SimpleHoTSelection[]): SimpleHoTSelection[] {
    const parentRowIds = new Set<number>();
    for (const item of selection) {
        if (item.parentRowId !== undefined) {
            parentRowIds.add(item.parentRowId);
        }
    }
    const result = selection.filter(x => !parentRowIds.has(x.row));
    return result;
}

export function getStatusText(driver: GroupedOperationalDriver | GroupedCustomDriver | GroupedLineItem) {
    let status = "Full Access";
    if (driver.valuesLocked === true) {
        status = "Read-Only";
    } else if (driver.locked === true) {
        status = "Edit-Only";
    } else if ("configLocked" in driver && driver.configLocked === true) {
        status = "Configurable";
    }
    return status;
}

